import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { UploadCloud, Loader2, X } from "lucide-react";
import { AppDispatch } from "../store/store";
import { selectUploads, reduxAddUpload } from "../store/dreamworldSlice";
import uploadImageToFirebase from "../utils/uploadImageToFirebase";
import { v4 as uuidv4 } from "uuid";
import { Timestamp } from "firebase/firestore";
import { selectUserGlobalData } from "../store/dreamworldSlice";
import { LibraryItem } from "../types/library";
import dreamWorldProvider from "../DreamWorldProvider";
import PageLayout from "../components/ui/PageLayout";

const UploadsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const dispatch = useDispatch<AppDispatch>();
  const uploads = useSelector(selectUploads);
  const userGlobalData = useSelector(selectUserGlobalData);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && userGlobalData) {
      const file = e.target.files[0];
      setIsUploading(true);

      try {
        const downloadUrl = await uploadImageToFirebase(file, "postsImages");
        const docId = uuidv4();
        const newUpload: LibraryItem = {
          id: docId,
          ownerId: userGlobalData.user.uid,
          type: "Image",
          fullUrl: downloadUrl,
          thumbnailUrl: downloadUrl,
          prompt: "",
          isFavorite: false,
          views: 0,
          likes: 0,
          tags: [],
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          createdAt: Timestamp.now(),
          isPublished: false,
          title: file.name,
        };
        await dreamWorldProvider.saveUploadItem(newUpload);
        dispatch(reduxAddUpload(newUpload));
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleUploadClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 h-full text-white">
      <PageLayout>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Uploads
            </h1>
            <p className="text-gray-400 mt-2 text-sm sm:text-base">
              Upload your creative assets and bring your dreams to life.
            </p>
          </div>
          <motion.button
            onClick={handleUploadClick}
            disabled={isUploading}
            className="flex items-center gap-2 p-2 bg-purple-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed mt-4 sm:mt-0"
            whileHover={!isUploading ? { scale: 1.05 } : {}}
            whileTap={!isUploading ? { scale: 0.95 } : {}}
          >
            {isUploading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <UploadCloud size={20} />
            )}
            <span>{isUploading ? "Uploading..." : "Upload Image"}</span>
          </motion.button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*"
          disabled={isUploading}
        />

        <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {uploads.map((item) => (
            <motion.div
              key={item.id}
              className="relative group rounded-lg overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSelectedImage(item.fullUrl)}
            >
              <img
                src={item.thumbnailUrl}
                alt={item.title}
                className="w-full h-auto object-cover aspect-square"
              />
            </motion.div>
          ))}
        </div>

        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white z-10 bg-black bg-opacity-50 rounded-full p-2"
            >
              <X size={24} />
            </button>
            <motion.img
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.5 }}
              src={selectedImage}
              alt="Full screen view"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </PageLayout>
    </div>
  );
};

export default UploadsPage;
